import React, { ReactElement, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Typography from 'antd/lib/typography';
import Tag from 'antd/lib/tag';
import Button from 'antd/lib/button';
import theme from 'antd/lib/theme';
import { 
  DownloadOutlined, 
  InfoCircleOutlined, 
  SafetyCertificateOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';
import RoundedModal from '../roundedModal/roundedModal';
import useMissingHubServicesInfo, { MissingServiceInfo } from '@hooks/useMissingHubServicesInfo';
import { selectMissingServicesModalVisible } from '@src/features/interface/interfaceSlice';
import { showMissingServicesModal } from '@src/features/interface/interfaceSlice';
import { selectCurrentRecipe } from '@src/features/Workspace/workspaceSlice';
import useTranslation from '@hooks/useTranslation';
import styles from './MissingServicesModal.module.css';
import SvgFromContent from '@components/SvgFromContent/SvgFromContent';
import { Badge, Tooltip } from 'antd';
import useMarketplaceNavigator from '@hooks/useMarketplaceNavigator';
import { ValidPlatformArch } from '@kemu-io/hs-types';
import useInstallMarketplaceItem from '@hooks/useInstallMarketplaceItem';
import StyledButton from '@components/form-control/styledButton/styledButton';

const { Text, Paragraph } = Typography;


/**
 * Modal component that displays a list of missing services required by the current recipe.
 * Shows services that are not installed, have lower versions, or have missing secrets.
 */
const MissingServicesModal = (): ReactElement => {
	const dispatch = useDispatch();
	const t = useTranslation('MissingServicesModal');
  const cw = useTranslation('CommonWords');
	const visible = useSelector(selectMissingServicesModalVisible);
  const { navigateToService } = useMarketplaceNavigator();
	const currentRecipe = useSelector(selectCurrentRecipe);
  const [userDismissed, setUserDismissed] = useState(false);
  const [installingAll, setInstallingAll] = useState(false);
  const [installInProgress, setInstallInProgress] = useState(false);
	const { missingServices, isLoading: loadingHubInfo, hasMissingServices } = useMissingHubServicesInfo();
  const { installHubService, installingService: installingServiceInfo } = useInstallMarketplaceItem();
  const {
    token: { colorPrimary },
  } = theme.useToken();


  const installingAnyService = installingAll || installInProgress;
  const multipleMissingServices = missingServices.length > 1 || installingAll;

  
  /**
   * Handles closing the modal
   */
  const handleClose = () => {
    dispatch(showMissingServicesModal(false));
    setUserDismissed(true);
  };

	/**
	 * Show modal when there are missing services and we're not loading
	 */
	useEffect(() => {
		// Only show modal if:
		// 1. We have a recipe loaded (poolId exists)
		// 2. Services are not loading
		// 3. There are actually missing services
		// 4. Modal is not already visible
		if (currentRecipe.poolId && !loadingHubInfo && hasMissingServices && !visible && !userDismissed) {
			dispatch(showMissingServicesModal(true));
		}
	}, [currentRecipe.poolId, loadingHubInfo, hasMissingServices, visible, dispatch, userDismissed]);

  // Close modal if there are no missing services
  useEffect(() => {
    if(!loadingHubInfo && missingServices.length === 0 && visible) {
      handleClose();
    }
  }, [missingServices, loadingHubInfo]);

  const handleOpenMarketplace = (serviceName: string, serviceVersion: string, platform: ValidPlatformArch) => {
    if (serviceName && serviceVersion && platform) {
      navigateToService({
        name: serviceName,
        version: serviceVersion,
        platform: platform
      });
    }
  };

	/**
	 * Handles installing a specific service
	 */
	const handleInstallService = async (service: MissingServiceInfo) => {
    try {
      setInstallInProgress(true);
      await installHubService({
        marketplaceItemId: service.marketplaceItemId,
        name: service.name,
        version: service.version,
      });
    } finally {
      setInstallInProgress(false);
    }
	};

	/**
	 * Handles installing all missing services
	 */
	const handleInstallAll = async () => {
    try {
      setInstallingAll(true);
      for (const service of missingServices) {
        await installHubService({
          marketplaceItemId: service.marketplaceItemId,
          name: service.name,
          version: service.version,
        });
      }
    } finally {
      setInstallingAll(false);
    }
	};

	/**
	 * Renders a service item in the list
	 */
	const renderServiceItem = (service: MissingServiceInfo) => {
		const getStatusInfo = () => {
			/* if (service.notInstalled) {
				return {
					color: 'var(--kemu-color-danger)',
					text: t('Status.NotInstalled'),
					icon: <ExclamationCircleOutlined />,
				};
			} else  */if (service.installedWithLowerVersion) {
				return {
					color: 'var(--kemu-color-warning)',
					text: t('Status.LowerVersion'),
					icon: <InfoCircleOutlined />,
				};
			} else if (service.installedWithMissingSecrets) {
				return {
					color: 'var(--kemu-color-warning)',
					text: t('Status.MissingSecrets'),
					icon: <InfoCircleOutlined />,
				};
			}
			return null;
		};

		const statusInfo = getStatusInfo();

		return (
			<div key={`${service.name}_${service.version}`} className={styles.ServiceCard}>
				<div className={styles.ServiceIcon}>
					<div className={styles.ServiceIconInner}>
						<SvgFromContent
              content={service.svgIcon}
              fill={colorPrimary}
              width={48}
              height={48}
            />
					</div>
				</div>
				
				<div className={styles.ServiceInfo}>
					<div className={styles.ServiceTitle}>
						<Text strong className={styles.ServiceName}>
							{service.displayName || service.name}
						</Text>
						{statusInfo && (
							<Tag color={statusInfo.color} icon={statusInfo.icon} className={styles.StatusTag}>
								{statusInfo.text}
							</Tag>
						)}
					</div>
					<div className={styles.ServiceProvider}>
            {service.isKemuOfficial && (
              <Tooltip title={`${cw('KemuOfficial')} ${cw('Account')}`}>
                <Badge
                  count={
                    <SafetyCertificateOutlined
                      style={{ color: colorPrimary }}
                    />
                  }
                />
              </Tooltip>
            )}
						<Text type="secondary">{service.author}</Text>
					</div>
				</div>

				<div className={styles.ServiceActions}>
          <Tooltip title={t('Actions.OpenMarketplace')}>
            <Button 
              icon={<ShoppingCartOutlined />} 
              onClick={() => handleOpenMarketplace(service.name, service.version, service.platform)} 
              type='text'
            />
          </Tooltip>

					<StyledButton
						color="primary"
            noShadow
            loading={installingServiceInfo?.name === service.name}
            disabled={installingAnyService}
						icon={<DownloadOutlined />}
						onClick={() => handleInstallService(service)}
            title={service.installedWithLowerVersion ? cw('Update') : t('Actions.Install')}
					/>
				</div>
			</div>
		);
	};

	return (
		<RoundedModal
			visible={visible}
			onCancel={handleClose}
			title={t('Title')}
			width={600}
			okBtnLabel={multipleMissingServices ? t('Actions.InstallAll') : t('Actions.Close')}
      okButtonColor={multipleMissingServices ? 'primary' : 'light'}
      closeOnMaskClick={false}
      disableOkButton={installingAnyService}
      loadingOkBtn={installingAll}
      cancelBtnLabel={multipleMissingServices ? t('Actions.Close') : undefined}
			onOk={!multipleMissingServices ? handleClose : handleInstallAll}
			className={styles.Modal}
		>
      <Paragraph className={styles.Subtitle}>{t('Subtitle', { total: missingServices.length })}</Paragraph>
			<div className={styles.ModalContent}>
        <div className={styles.MissingServicesContent}>
          <div className={styles.ServicesList}>
            {missingServices.map(renderServiceItem)}
          </div>
        </div>
			</div>
		</RoundedModal>
	);
};

export default MissingServicesModal;
